/**
 * Where every box and every trace goes. No React, no SVG, no DOM.
 *
 * This file is separate from the components that draw it for one reason: an
 * off-by-one in a layout renders something that looks entirely plausible and is
 * wrong. A render test sees a diagram and passes. Arithmetic asserted directly
 * is the only thing that catches a band placed one row too low or a row that is
 * centred half a gap off, so everything here is a pure exported function and
 * `layout.test.ts` asserts on the numbers.
 *
 * The model is deliberately flat: a node knows its band (which row of the
 * diagram it belongs in) and the key of its parent, and nothing else. Both
 * diagrams reduce to that, which is why one layout serves requirements laid out
 * by level and parts laid out by containment depth.
 */

import type { DesignRow, FlowdownRow } from '../../schema'

/** A node before it has been placed: what to draw, and what it hangs from. */
export interface DiagramNode {
  /** Unique within one diagram. Not necessarily the model's identifier. */
  key: string
  /** The model's short name, for linking and for monospace display. */
  id: string
  /** The heading drawn in the box. */
  label: string
  /** The second line, wrapped and clipped by the renderer. */
  detail: string
  /** Which row of the diagram this belongs in — a level, or a depth. */
  band: number
  /** The key this node traces up to, or null. */
  /**
   * Every node this one derives from.
   *
   * A LIST since issue #32: a requirement may name several sources and no
   * source is primary, so the hierarchy is a DAG rather than a tree and a
   * scalar here would have to pick a winner the model does not name. Parts
   * and other single-parent diagrams simply pass a list of one.
   */
  parentKeys: string[]
  /**
   * True for a requirement that should have a parent and has none.
   *
   * Drawn, and marked — not hidden. An orphan is the finding; a diagram that
   * omits it reports a tidier model than the one that exists.
   */
  isOrphan: boolean
  /** Free-form tag the renderer colours by (a status, a definition flag). */
  tone: string
}

export interface PlacedNode extends DiagramNode {
  x: number
  y: number
  width: number
  height: number
}

export interface PlacedEdge {
  key: string
  fromKey: string
  toKey: string
  /** An SVG path `d`, child's top edge curving up to its parent's bottom edge. */
  path: string
}

export interface PlacedBand {
  band: number
  label: string
  y: number
  height: number
}

export interface Diagram {
  nodes: PlacedNode[]
  edges: PlacedEdge[]
  bands: PlacedBand[]
  width: number
  height: number
}

export interface LayoutMetrics {
  nodeWidth: number
  nodeHeight: number
  /** Between two boxes in the same band. */
  gapX: number
  /** Between one band and the next — the room the traces are drawn in. */
  gapY: number
  padX: number
  padY: number
  /** The strip on the left the band labels are written in. */
  gutter: number
  /**
   * The most boxes one row of a band holds before it wraps onto another row.
   *
   * Absent means never wrap. Issue #93: one row per band put a few hundred L3
   * and L4 requirements into a strip tens of thousands of units wide, and
   * fitting that into a pane drew every box a few pixels across. Wrapping makes
   * a big band TALL instead, which a pane can pan down and still read.
   */
  maxPerRow?: number
  /** Between two wrapped rows of the SAME band. Smaller than gapY on purpose. */
  rowGap?: number
}

export const DEFAULT_METRICS: LayoutMetrics = {
  nodeWidth: 184,
  nodeHeight: 60,
  gapX: 28,
  gapY: 88,
  padX: 16,
  padY: 16,
  gutter: 80,
  // Eight boxes is 8 * 184 + 7 * 28 = 1668 units, plus the gutter and padding
  // 1780: drawn about two-thirds natural size in a 1200-pixel pane, which is
  // still legible fitted. Wider and the fitted view stops being readable.
  maxPerRow: 8,
  rowGap: 24,
}

/**
 * The band a requirement with no stated level goes in.
 *
 * A number rather than a separate case so it sorts last with everything else,
 * and far enough above any real level that a program inventing L5 does not
 * collide with it.
 */
export const UNLEVELLED_BAND = 99

/**
 * Place nodes into bands, and draw a trace from each to its parent.
 *
 * Bands are laid out by their POSITION in the sorted set of bands present, not
 * by their number: a program with no L1 gets four rows, not five with a blank.
 * A blank stripe reads as missing data rather than as an absent level.
 *
 * Within a band, order follows the parent's order in the band above, so traces
 * cross as little as the single pass allows. This is not a crossing-minimising
 * layout and does not claim to be — it is one stable pass, which is enough for
 * a few hundred boxes and is something a test can state exactly. A node whose
 * parent is not placed (an orphan, or a parent lower down the diagram) sorts to
 * the right-hand end of its band.
 *
 * A band with more than `metrics.maxPerRow` boxes WRAPS onto further rows, in
 * the same order, read left to right and then down. Each row is centred like a
 * band of its own, and the band is as tall as its rows; the next band starts
 * below the last of them.
 */
export function layout(
  nodes: DiagramNode[],
  bandLabels: Record<number, string> = {},
  metrics: LayoutMetrics = DEFAULT_METRICS,
): Diagram {
  if (nodes.length === 0) {
    return { nodes: [], edges: [], bands: [], width: 0, height: 0 }
  }

  const byBand = new Map<number, DiagramNode[]>()
  for (const node of nodes) {
    const group = byBand.get(node.band)
    if (group) group.push(node)
    else byBand.set(node.band, [node])
  }
  const bandNumbers = [...byBand.keys()].sort((a, b) => a - b)

  // Ordering pass. positionOf accumulates as bands are settled, so a child two
  // bands below its parent still follows it — the parent need only have been
  // placed already, not be immediately above.
  const positionOf = new Map<string, number>()
  const ordered = new Map<number, DiagramNode[]>()
  for (const band of bandNumbers) {
    const group = byBand.get(band) as DiagramNode[]
    const ranked = group.map((node, index) => ({
      node,
      index,
      // Ordered under the leftmost source that has already been placed. With
      // several sources a node can only sit in one column, so this is a
      // LAYOUT choice and not a statement about which source is primary —
      // every source still gets its own trace drawn below.
      under: Math.min(
        ...node.parentKeys
          .filter((key) => positionOf.has(key))
          .map((key) => positionOf.get(key) as number),
        Number.POSITIVE_INFINITY,
      ),
    }))
    ranked.sort((a, b) => a.under - b.under || a.index - b.index)
    const settled = ranked.map((entry) => entry.node)
    settled.forEach((node, index) => positionOf.set(node.key, index))
    ordered.set(band, settled)
  }

  const perRow =
    metrics.maxPerRow && metrics.maxPerRow > 0 ? Math.floor(metrics.maxPerRow) : Number.POSITIVE_INFINITY
  const rowGap = metrics.rowGap ?? metrics.gapX
  const rowWidth = (count: number) =>
    count * metrics.nodeWidth + Math.max(0, count - 1) * metrics.gapX
  const rowsIn = (count: number) => Math.max(1, Math.ceil(count / perRow))
  const bandHeight = (count: number) =>
    rowsIn(count) * metrics.nodeHeight + (rowsIn(count) - 1) * rowGap
  const widest = Math.max(
    ...bandNumbers.map((band) => rowWidth(Math.min(perRow, (ordered.get(band) as DiagramNode[]).length))),
  )

  const placed: PlacedNode[] = []
  const bands: PlacedBand[] = []

  let y = metrics.padY
  for (const band of bandNumbers) {
    const group = ordered.get(band) as DiagramNode[]
    const height = bandHeight(group.length)
    bands.push({ band, label: bandLabels[band] ?? String(band), y, height })
    group.forEach((node, index) => {
      // Modulo and division rather than index - row * perRow: with no limit
      // perRow is Infinity, and 0 * Infinity is NaN.
      const row = Math.floor(index / perRow)
      const column = index % perRow
      const inRow = row === 0 ? Math.min(perRow, group.length) : Math.min(perRow, group.length - row * perRow)
      // Centred within the widest band, so the diagram reads as a pyramid rather
      // than as a left-aligned staircase - and each wrapped row on its own, so a
      // short last row sits in the middle rather than hanging off the left.
      // Rounded, because a half pixel in an SVG coordinate is a blurred edge on
      // a non-retina screen.
      const startX = metrics.gutter + metrics.padX + Math.round((widest - rowWidth(inRow)) / 2)
      placed.push({
        ...node,
        x: startX + column * (metrics.nodeWidth + metrics.gapX),
        y: y + row * (metrics.nodeHeight + rowGap),
        width: metrics.nodeWidth,
        height: metrics.nodeHeight,
      })
    })
    y += height + metrics.gapY
  }

  const byKey = new Map(placed.map((node) => [node.key, node]))
  const edges: PlacedEdge[] = []
  for (const node of placed) {
    // ONE TRACE PER SOURCE. A requirement driven by two others shows two
    // lines going up, which is the whole point of the change.
    for (const parentKey of node.parentKeys) {
      const parent = byKey.get(parentKey)
      // A source named but not present is not drawn. It happens: a requirement
      // may name one outside the tree the endpoint answered. Inventing a box
      // for it would be worse than the missing line.
      if (!parent) continue
      edges.push({
        key: `${parentKey}->${node.key}`,
        fromKey: node.key,
        toKey: parentKey,
        path: tracePath(node, parent),
      })
    }
  }

  return {
    nodes: placed,
    edges,
    bands,
    width: metrics.gutter + metrics.padX * 2 + widest,
    height:
      metrics.padY * 2 +
      bands.reduce((total, band) => total + band.height, 0) +
      Math.max(0, bands.length - 1) * metrics.gapY,
  }
}

/**
 * The cubic from a child's top edge to its parent's bottom edge.
 *
 * Control points are pulled straight up and straight down, so the curve leaves
 * and arrives vertically and the arrow head at the parent points at the box.
 * The bend is clamped so that a trace to a parent that is NOT above the child
 * (an inconsistent level, which the model permits) still draws a visible curve
 * instead of collapsing to a straight line through both boxes.
 */
export function tracePath(child: PlacedNode, parent: PlacedNode): string {
  const childX = Math.round(child.x + child.width / 2)
  const childY = child.y
  const parentX = Math.round(parent.x + parent.width / 2)
  const parentY = parent.y + parent.height
  const bend = Math.max(16, Math.round((childY - parentY) / 2))
  return `M ${childX} ${childY} C ${childX} ${childY - bend}, ${parentX} ${parentY + bend}, ${parentX} ${parentY}`
}

// ---------------------------------------------------------------------------
// The two builders. Each turns one endpoint's rows into the flat node model
// above, and neither knows anything about pixels.
// ---------------------------------------------------------------------------

/**
 * Requirements, banded by their stated level.
 *
 * The band is the LEVEL, not the row's depth: "L0 at the top" is a statement
 * about the level, and a requirement whose source is two levels above it should
 * sit in its own level's row rather than one below it. Depth is therefore
 * ignored here, and the sources come from the `sources` field, which
 * /api/flowdown carries on every row.
 *
 * SEVERAL SOURCES PER REQUIREMENT since issue #32, each drawn as its own trace.
 *
 * An orphan — no source, and below L0 or with no level at all — is exactly what
 * `repository.orphans` reports, and it is banded and drawn like anything else.
 */
export function requirementNodes(rows: FlowdownRow[]): DiagramNode[] {
  const nodes: DiagramNode[] = []
  const seen = new Set<string>()

  for (const row of rows) {
    const key = requirementKey(row)
    if (!key || seen.has(key)) continue
    seen.add(key)

    const level = typeof row.level === 'number' ? row.level : null
    const sources = Array.isArray(row.sources)
      ? row.sources.filter((entry): entry is string => typeof entry === 'string' && !!entry)
      : []
    const status = String(row.status ?? '')

    nodes.push({
      key,
      id: row.id,
      label: row.id || row.name,
      detail: String(row.statement ?? row.name ?? ''),
      band: level ?? UNLEVELLED_BAND,
      parentKeys: sources,
      isOrphan: sources.length === 0 && (level === null || level > 0),
      tone: row.is_unresolved ? 'unresolved' : status,
    })
  }
  return nodes
}

/**
 * The key `requirementNodes` gives a row: its identifier, else its qualified name.
 *
 * Exported so that anything choosing requirements by a field - a focus on a
 * segment - names them exactly as the diagram does.
 */
export function requirementKey(row: FlowdownRow): string {
  return row.id || row.qualified_name
}

/** The two requirement fields a diagram can be focused on (issue #93). */
export type RequirementFacet = 'segment' | 'subsystem'

/**
 * Every requirement stating `value` for a segment or a subsystem, by key.
 *
 * The SEEDS of a focus: `focus.lineage` then adds what they derive from and
 * what derives from them, so a subsystem's focus still shows the L0 it serves.
 * Compared exactly, as the model states it - these are enumerated values.
 */
export function requirementSeeds(rows: FlowdownRow[], facet: RequirementFacet, value: string): string[] {
  const seeds = new Set<string>()
  for (const row of rows) {
    if (String(row[facet] ?? '') !== value) continue
    const key = requirementKey(row)
    if (key) seeds.add(key)
  }
  return [...seeds]
}

/**
 * The values a segment or subsystem takes across the rows, for the picker.
 *
 * Blank is left out: "no subsystem" is every L0-L2 requirement, which is not a
 * focus. Natural order, so `sub10` follows `sub9`.
 */
export function facetValues(rows: FlowdownRow[], facet: RequirementFacet): string[] {
  const values = new Set<string>()
  for (const row of rows) {
    const value = String(row[facet] ?? '')
    if (value) values.add(value)
  }
  return [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
}

/** `L0`…`L4`, plus the row that holds requirements stating no level. */
export function requirementBandLabels(nodes: DiagramNode[]): Record<number, string> {
  const labels: Record<number, string> = {}
  for (const node of nodes) {
    labels[node.band] = node.band === UNLEVELLED_BAND ? 'No level' : `L${node.band}`
  }
  return labels
}

/**
 * Parts, banded by containment depth.
 *
 * /api/design already answers depth-first rows carrying `depth`, so the parent
 * is the nearest preceding row that is shallower — a stack walk, not a graph
 * solve. Written here rather than shared with `Flowdown.toTree` because that
 * builds nested nodes and this needs parent keys; the algorithm is four lines
 * either way.
 *
 * The key cannot be the identifier or the qualified name. `design_tree` steps
 * through a usage's definition, so a definition's children are walked once per
 * usage of it: `Guidance`'s parts appear under every `part g : Guidance`, and
 * two boxes legitimately carry the same qualified name. The row's position is
 * the only thing that is unique.
 */
export function designNodes(rows: DesignRow[]): DiagramNode[] {
  const nodes: DiagramNode[] = []
  /** Key of the last node seen at each depth, so index N is depth N's parent. */
  const stack: string[] = []

  rows.forEach((row, index) => {
    const key = `${index}:${row.qualified_name}`
    // Drop back to this row's parent. A row deeper than its predecessor by more
    // than one still lands under the deepest thing above it rather than being
    // dropped, which is the same forgiving rule Flowdown.toTree uses.
    while (stack.length > row.depth) stack.pop()
    const parentKey = stack.length ? stack[stack.length - 1] : null
    stack.push(key)

    const typed = row.type_name
      ? `: ${row.type_name}${row.multiplicity ? ` ${row.multiplicity}` : ''}`
      : row.multiplicity
    const ports = row.ports?.length ?? 0

    nodes.push({
      key,
      id: row.id,
      label: row.id || row.name,
      detail: ports ? `${typed} · ${ports} port${ports === 1 ? '' : 's'}` : typed,
      band: row.depth,
      parentKeys: parentKey === null ? [] : [parentKey],
      // A part at depth 0 is a root of the decomposition, not an orphan. There
      // is nothing above it that it failed to trace to.
      isOrphan: false,
      tone: row.is_definition ? 'definition' : 'usage',
    })
  })
  return nodes
}

/** `Top level`, then `Contained in …` by depth. */
export function designBandLabels(nodes: DiagramNode[]): Record<number, string> {
  const labels: Record<number, string> = {}
  for (const node of nodes) {
    labels[node.band] = node.band === 0 ? 'Top level' : `Depth ${node.band}`
  }
  return labels
}
